import { ReactElement } from 'react'
import { Box, Tooltip, useTheme } from '@mui/material'

/** Fits a force like "-99,99 kN" including the highlight chip; part of the first column. */
const METRIC_COLUMN_WIDTH_PX = 68

type Props = {
  value: string
  /** Unit rendered muted after the value (e.g. "kN"). */
  unit?: string
  tooltip?: string
  /** Tints the value like an η chip, for a value that is notably good. */
  highlight?: 'success'
}

/**
 * Numeric value at the right edge of a list row, next to the η stack — the anchors list shows
 * the resulting design tensile load there. Fixed width so the values line up across rows and the
 * η stack keeps its place regardless of how wide the number is.
 */
export const RowMetric = ({ value, unit, tooltip, highlight }: Props): ReactElement => {
  const theme = useTheme()

  return (
    <Tooltip title={tooltip ?? ''} placement="left" arrow disableInteractive>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'flex-end',
          gap: 0.25,
          flexShrink: 0,
          width: METRIC_COLUMN_WIDTH_PX,
          fontSize: 13,
          fontWeight: 500,
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1.2,
          whiteSpace: 'nowrap',
          color: theme.palette.text.primary,
        }}
      >
        <Box
          component="span"
          sx={
            highlight === 'success'
              ? {
                  px: 0.5,
                  borderRadius: 0.25,
                  background: 'rgba(55,132,52,0.14)',
                  color: theme.palette.success.dark,
                }
              : undefined
          }
        >
          {value}
        </Box>
        {unit ? (
          <Box component="span" sx={{ fontSize: 10, color: theme.palette.text.secondary }}>
            {unit}
          </Box>
        ) : null}
      </Box>
    </Tooltip>
  )
}
